import { differenceInCalendarDays, format, parseISO } from "date-fns";
import type {
  AccountStatus,
  CalculatedReportSummary,
  DailyRevenueRecord,
  ExpenseRecord,
  ReportingPeriod,
  TenantAccountRecord,
} from "../../types/report";
import { MONEY_TOLERANCE } from "../normalization/normalize";

export const CREDIT_CARD_PROCESSING_FEE_RATE = 0.035;

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function calculateAccountStatus(
  amountDue: number,
  amountReceived: number,
  movedOut: boolean,
): AccountStatus {
  if (movedOut) return "moved_out";
  if (
    Math.abs(amountDue) < MONEY_TOLERANCE &&
    Math.abs(amountReceived) < MONEY_TOLERANCE
  ) {
    return "no_charge";
  }
  const balance = amountDue - amountReceived;
  if (Math.abs(balance) < MONEY_TOLERANCE) return "paid";
  if (amountReceived <= 0 && amountDue > 0) return "unpaid";
  if (balance > MONEY_TOLERANCE) return "partially_paid";
  return "overpaid";
}

export function calculateDailyRecord(
  record: DailyRevenueRecord,
): DailyRevenueRecord {
  return {
    ...record,
    dailyTotal: Object.values(record.income).reduce(
      (sum, amount) => sum + amount,
      0,
    ),
  };
}

export function calculateDailyColumnTotals(records: DailyRevenueRecord[]): {
  income: Record<string, number>;
  startingTicket: number;
  endingTicket: number;
  totalTicket: number;
  dailyTotal: number;
} {
  return records.reduce(
    (totals, record) => {
      Object.entries(record.income).forEach(([label, amount]) => {
        totals.income[label] = (totals.income[label] ?? 0) + amount;
      });
      totals.startingTicket += record.startingTicket;
      totals.endingTicket += record.endingTicket;
      totals.totalTicket += record.totalTicket;
      totals.dailyTotal += record.dailyTotal;
      return totals;
    },
    {
      income: {} as Record<string, number>,
      startingTicket: 0,
      endingTicket: 0,
      totalTicket: 0,
      dailyTotal: 0,
    },
  );
}

export function calculateTenantRecord(
  record: TenantAccountRecord,
): TenantAccountRecord {
  const methodTotal =
    record.checkAmount +
    record.creditCardAmount +
    record.zelleAmount +
    record.wireAchAmount +
    record.cashAmount +
    record.kioskAmount;
  const amountReceived = methodTotal;
  return {
    ...record,
    amountReceived,
    balance: record.amountDue - amountReceived,
    status: calculateAccountStatus(
      record.amountDue,
      amountReceived,
      record.status === "moved_out",
    ),
  };
}

export function reportingPeriodFromDaily(
  records: DailyRevenueRecord[],
): ReportingPeriod | null {
  const dates = records.map((record) => record.date).sort();
  if (!dates.length) return null;
  const startDate = dates[0]!;
  const endDate = dates[dates.length - 1]!;
  return {
    startDate,
    endDate,
    monthLabel: format(parseISO(startDate), "MMMM yyyy"),
  };
}

export function calculateSummary(
  daily: DailyRevenueRecord[],
  tenants: TenantAccountRecord[],
  expenses: ExpenseRecord[],
  reportingPeriod: ReportingPeriod | null,
): CalculatedReportSummary {
  const calculatedDaily = daily.map(calculateDailyRecord);
  const calculatedTenants = tenants.map(calculateTenantRecord);
  const incomeTotals = new Map<string, number>();
  calculatedDaily.forEach((row) =>
    Object.entries(row.income).forEach(([label, amount]) =>
      incomeTotals.set(label, (incomeTotals.get(label) ?? 0) + amount),
    ),
  );
  const dailyIncomeByMethod = [...incomeTotals].map(([label, value]) => ({
    label,
    value,
  }));
  const totalDailyRevenue = dailyIncomeByMethod.reduce(
    (sum, method) => sum + method.value,
    0,
  );
  const monthlyCheckRevenue = calculatedTenants.reduce(
    (sum, row) => sum + row.checkAmount,
    0,
  );
  const monthlyCreditCardRevenue = calculatedTenants.reduce(
    (sum, row) => sum + row.creditCardAmount,
    0,
  );
  const monthlyOtherRevenue = calculatedTenants.reduce(
    (sum, row) =>
      sum +
      row.zelleAmount +
      row.wireAchAmount +
      row.cashAmount +
      row.kioskAmount,
    0,
  );
  const totalMonthlyRevenue = calculatedTenants.reduce(
    (sum, row) => sum + row.amountReceived,
    0,
  );
  const validationRevenue = dailyIncomeByMethod.reduce(
    (sum, method) =>
      ["validation", "validation amount"].includes(
        method.label.trim().toLowerCase(),
      )
        ? sum + method.value
        : sum,
    0,
  );
  const totalIncome = totalDailyRevenue + totalMonthlyRevenue;
  const totalTenantCharges = calculatedTenants.reduce(
    (sum, row) => sum + row.amountDue,
    0,
  );
  const totalTenantPayments = totalMonthlyRevenue;
  const outstandingBalance = calculatedTenants.reduce(
    (sum, row) => sum + Math.max(0, row.balance),
    0,
  );
  const dailyCreditCardRevenue = dailyIncomeByMethod.reduce(
    (sum, method) =>
      ["credit card", "credit", "card"].includes(
        method.label.trim().toLowerCase(),
      )
        ? sum + method.value
        : sum,
    0,
  );
  const creditCardProcessingBase =
    dailyCreditCardRevenue + monthlyCreditCardRevenue;
  const creditCardProcessingFee = roundMoney(
    creditCardProcessingBase * CREDIT_CARD_PROCESSING_FEE_RATE,
  );
  const importedExpenses = expenses.reduce((sum, row) => sum + row.amount, 0);
  const totalExpenses = importedExpenses + creditCardProcessingFee;
  const active = calculatedDaily.filter(
    (row) => row.dailyTotal > MONEY_TOLERANCE,
  );
  const highest = active.reduce<DailyRevenueRecord | null>(
    (best, row) => (!best || row.dailyTotal > best.dailyTotal ? row : best),
    null,
  );
  const lowest = active.reduce<DailyRevenueRecord | null>(
    (best, row) => (!best || row.dailyTotal < best.dailyTotal ? row : best),
    null,
  );
  const calendarDays = reportingPeriod
    ? differenceInCalendarDays(
        parseISO(reportingPeriod.endDate),
        parseISO(reportingPeriod.startDate),
      ) + 1
    : calculatedDaily.length;

  return {
    dailyIncomeByMethod,
    totalDailyRevenue,
    monthlyCheckRevenue,
    monthlyCreditCardRevenue,
    monthlyOtherRevenue,
    totalMonthlyRevenue,
    validationRevenue,
    totalIncome,
    totalTenantCharges,
    totalTenantPayments,
    outstandingBalance,
    collectionRate:
      totalTenantCharges > MONEY_TOLERANCE
        ? (totalTenantPayments / totalTenantCharges) * 100
        : 0,
    creditCardProcessingBase,
    creditCardProcessingFee,
    totalExpenses,
    netIncome: totalIncome - totalExpenses,
    activeRevenueDays: active.length,
    calendarDays,
    averageActiveDayRevenue: active.length
      ? totalDailyRevenue / active.length
      : 0,
    averageCalendarDayRevenue: calendarDays
      ? totalDailyRevenue / calendarDays
      : 0,
    highestRevenueDate: highest?.date ?? null,
    highestRevenueAmount: highest?.dailyTotal ?? 0,
    lowestActiveRevenueDate: lowest?.date ?? null,
    lowestActiveRevenueAmount: lowest?.dailyTotal ?? 0,
  };
}
